/**
 * Lecture d'une programmation copiée telle quelle.
 *
 * L'import CSV suppose des colonnes ; or une programmation se trouve le plus
 * souvent sous forme de texte sur un site ou dans un PDF, en blocs :
 *
 *     VENDREDI 4 AOÛT
 *     Grande scène
 *     18h30  Artiste A
 *     20h00 – 21h30  Artiste B
 *
 * Ce module en tire une grille. Il ne prétend pas avoir raison : sa sortie est
 * relue par un humain avant tout enregistrement (CONTENU.md §1).
 *
 * **Rien n'est jamais écarté en silence.** Une ligne non comprise est rendue
 * telle quelle, avec son numéro : c'est la seule façon pour la personne qui
 * relit de savoir ce qui manque. Un analyseur qui perd discrètement trois
 * concerts est pire qu'un analyseur qui échoue.
 */

/** Jours de la semaine dans les langues livrées, sans accent ni casse. */
const WEEKDAYS = [
  // fr
  "lundi",
  "mardi",
  "mercredi",
  "jeudi",
  "vendredi",
  "samedi",
  "dimanche",
  // en
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
  // es
  "lunes",
  "martes",
  "miercoles",
  "jueves",
  "viernes",
  "sabado",
  "domingo",
  // de
  "montag",
  "dienstag",
  "mittwoch",
  "donnerstag",
  "freitag",
  "samstag",
  "sonntag",
  // nl
  "maandag",
  "dinsdag",
  "woensdag",
  "donderdag",
  "vrijdag",
  "zaterdag",
  "zondag",
];

/** Retire accents et casse, pour comparer des mots écrits de dix façons. */
function fold(text: string): string {
  return text.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
}

/**
 * Heures reconnues : `18:30`, `18h30`, `18 h 30`, `18h`, `8:05`.
 *
 * Le point comme séparateur (`18.30`) est volontairement absent : il se
 * confondrait avec une date (`4.08`) et produirait des concerts fantômes.
 */
const TIME = /\b([01]?\d|2[0-3])\s*(?:[:h])\s*([0-5]\d)?\b/;
const TIME_GLOBAL = new RegExp(TIME.source, "g");

function normaliseTime(hours: string, minutes: string | undefined): string {
  return `${hours.padStart(2, "0")}:${(minutes ?? "00").padStart(2, "0")}`;
}

export type ParsedRow = {
  /** Intitulé du jour, tel qu'écrit dans la source. */
  day: string;
  /** Scène courante, si la source en annonce une. */
  stage: string;
  artist: string;
  start: string;
  end?: string;
  /** Ligne d'origine, pour que la relecture puisse retrouver le contexte. */
  line: number;
};

export type ParsedLineup = {
  rows: ParsedRow[];
  /** Lignes non comprises, rendues intactes plutôt qu'écartées. */
  unknown: { line: number; text: string }[];
};

/** Vrai si la ligne annonce un jour. */
function isDayHeading(text: string): boolean {
  const folded = fold(text);
  if (WEEKDAYS.some((day) => folded.includes(day))) return true;
  // Une date seule fait aussi un intitulé de jour : « 04/08 », « 4 août 2027 ».
  return /^\s*\d{1,2}[/.-]\d{1,2}([/.-]\d{2,4})?\s*$/.test(text);
}

/**
 * Vrai si la ligne annonce une scène.
 *
 * Faute de marqueur fiable, on procède par élimination : ni heure, ni jour, et
 * assez courte pour être un intitulé. Un nom d'artiste isolé sur sa ligne sera
 * pris pour une scène — d'où la relecture.
 */
function isStageHeading(text: string): boolean {
  return !TIME.test(text) && !isDayHeading(text) && text.trim().length <= 60;
}

/**
 * Sépare l'artiste des heures qui l'accompagnent.
 *
 * Les heures sont retirées où qu'elles soient — avant, après, des deux côtés —
 * puis la ponctuation de liaison qui les reliait au nom.
 */
function extractArtist(text: string): string {
  return text
    .replace(TIME_GLOBAL, " ")
    .replace(/[–—-]{1,2}/g, " ")
    .replace(/[·•|]/g, " ")
    .replace(/\s{2,}/g, " ")
    .trim();
}

/**
 * Analyse un programme collé.
 *
 * Le jour et la scène se propagent aux lignes suivantes : c'est ainsi qu'une
 * programmation s'écrit, en blocs sous un intitulé.
 */
export function parseLineupText(text: string): ParsedLineup {
  const rows: ParsedRow[] = [];
  const unknown: { line: number; text: string }[] = [];

  let day = "";
  let stage = "";

  text.split(/\r?\n/).forEach((raw, index) => {
    const line = index + 1;
    const trimmed = raw.trim();
    if (trimmed === "") return;

    if (isDayHeading(trimmed) && !TIME.test(trimmed)) {
      day = trimmed;
      // Changer de jour remet la scène à zéro : la suivante sera annoncée.
      stage = "";
      return;
    }

    const times = [...trimmed.matchAll(TIME_GLOBAL)];
    if (times.length === 0) {
      if (isStageHeading(trimmed)) {
        stage = trimmed;
      } else {
        unknown.push({ line, text: trimmed });
      }
      return;
    }

    const artist = extractArtist(trimmed);
    if (artist === "") {
      // Une heure sans nom n'est pas un concert : le plus souvent un horaire
      // d'ouverture ou un intitulé de créneau.
      unknown.push({ line, text: trimmed });
      return;
    }

    const first = times[0]!;
    const second = times[1];

    rows.push({
      day,
      stage,
      artist,
      start: normaliseTime(first[1]!, first[2]),
      ...(second ? { end: normaliseTime(second[1]!, second[2]) } : {}),
      line,
    });
  });

  return { rows, unknown };
}

/**
 * Signale ce qui empêchera l'enregistrement.
 *
 * Distinct de l'analyse : celle-ci décrit ce qu'elle a lu, celle-là dit ce qui
 * manque. Une ligne sans jour n'est pas mal lue — la source ne le donnait pas.
 */
export function lineupGaps(parsed: ParsedLineup): string[] {
  const gaps: string[] = [];

  if (parsed.rows.length === 0) gaps.push("Aucun passage reconnu.");
  if (parsed.rows.some((row) => row.day === "")) {
    gaps.push(
      "Des passages n'ont pas de jour : ajoutez un intitulé de jour au-dessus.",
    );
  }
  if (parsed.rows.some((row) => row.stage === "")) {
    gaps.push(
      "Des passages n'ont pas de scène : ajoutez un intitulé de scène au-dessus.",
    );
  }
  if (parsed.unknown.length > 0) {
    gaps.push(`${parsed.unknown.length} ligne(s) non comprise(s), à vérifier.`);
  }

  return gaps;
}
